import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
} from 'react';
import { useSelector } from 'react-redux';
import { Dropdown } from 'react-bootstrap';
import classnames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell, faBellRing, faUserPlus, faUsers,
} from '@fortawesome/pro-regular-svg-icons';

import controlsStyle from '../../EventControls/style.module.scss';
import { playUserJoinBeep, playPeakBeep } from './sounds';

import style from './style.module.scss';

const NEW_USERS_KEY = 'monitorQueueAlertEnabled';
const PEAK_KEY = 'metricsAlertPeak';
const PEAK_THRESHOLD_KEY = 'metricsAlertPeakThreshold';
const DEFAULT_PEAK_THRESHOLD = 20;

const skipNext = { queue: true, peak: true };

const readBool = (key) => localStorage.getItem(key) === 'true';

const readThreshold = () => {
  const stored = Number(localStorage.getItem(PEAK_THRESHOLD_KEY));
  return stored > 0 ? stored : DEFAULT_PEAK_THRESHOLD;
};

const hasQueueJoin = (prevQueues = {}, nextQueues = {}) => (
  Object.keys({ ...prevQueues, ...nextQueues }).some((cam) => {
    const prev = prevQueues[cam] || [];
    const next = nextQueues[cam] || [];
    return next.length > prev.length || next.some((id) => !prev.includes(id));
  })
);

const runAlertCheck = ({
  active,
  skipKey,
  prev,
  value,
  shouldAlert,
  onAlert,
}) => {
  if (!active) {
    return value;
  }

  if (skipNext[skipKey]) {
    skipNext[skipKey] = false;
    return value;
  }

  if (prev != null && shouldAlert(prev, value)) {
    onAlert();
  }

  return value;
};

export const AlertsListener = () => {
  const queue = useSelector(({ cameras: { queue: cameraQueue } }) => cameraQueue);
  const peakUsers = useSelector(({ eventMetrics: { data } }) => data.peak ?? 0);
  const isAdmin = useSelector(({ authentication: { roles = [] } }) => roles.includes('admin'));

  const prevQueueRef = useRef(null);
  const prevPeakRef = useRef(peakUsers);

  useEffect(() => {
    prevQueueRef.current = runAlertCheck({
      active: isAdmin && readBool(NEW_USERS_KEY),
      skipKey: 'queue',
      prev: prevQueueRef.current,
      value: queue,
      shouldAlert: (prev, next) => hasQueueJoin(prev, next),
      onAlert: playUserJoinBeep,
    });
  }, [queue, isAdmin]);

  useEffect(() => {
    prevPeakRef.current = runAlertCheck({
      active: isAdmin && readBool(PEAK_KEY),
      skipKey: 'peak',
      prev: prevPeakRef.current,
      value: peakUsers,
      shouldAlert: (prev, next) => prev < readThreshold() && next >= readThreshold(),
      onAlert: playPeakBeep,
    });
  }, [peakUsers, isAdmin]);

  return null;
};

const AlertsToggle = forwardRef(({ active, className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={classnames(style.toggle, 'hide-arrow', className)}
    title="Alerts"
    {...props}
  >
    <FontAwesomeIcon icon={active ? faBellRing : faBell} />
  </button>
));

const AlertDropdownItem = forwardRef(({ children }, ref) => (
  <div ref={ref} className={style.item}>
    {children}
  </div>
));

const AlertRow = ({
  icon, label, checked, onChange, children,
}) => (
  <Dropdown.Item as={AlertDropdownItem} onClick={(e) => e.preventDefault()}>
    <div className={controlsStyle.cameraItem}>
      <div className={style.rowLabel}>
        <FontAwesomeIcon icon={icon} />
        <span>{label}</span>
      </div>
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label className="switcher">
        <input
          type="checkbox"
          checked={checked}
          className="switcher-input"
          onChange={onChange}
        />
        <span className="switcher-indicator">
          <span className="switcher-yes" />
          <span className="switcher-no" />
        </span>
      </label>
    </div>
    {children}
  </Dropdown.Item>
);

const AlertsDropdown = () => {
  const isAdmin = useSelector(({ authentication: { roles = [] } }) => roles.includes('admin'));
  const [newUsersEnabled, setNewUsersEnabled] = useState(() => readBool(NEW_USERS_KEY));
  const [peakEnabled, setPeakEnabled] = useState(() => readBool(PEAK_KEY));
  const [peakThreshold, setPeakThreshold] = useState(readThreshold);

  const toggleSetting = (key, skipKey, setter) => {
    setter((prev) => {
      const next = !prev;
      localStorage.setItem(key, String(next));
      if (next) {
        skipNext[skipKey] = true;
      }
      return next;
    });
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <Dropdown className={style.root}>
      <Dropdown.Toggle
        as={AlertsToggle}
        active={newUsersEnabled || peakEnabled}
      />
      <Dropdown.Menu
        alignRight
        className={classnames(controlsStyle.dropdownContainer, style.menu)}
        popperConfig={{ strategy: 'fixed' }}
      >
        <AlertRow
          icon={faUserPlus}
          label="New users"
          checked={newUsersEnabled}
          onChange={() => toggleSetting(NEW_USERS_KEY, 'queue', setNewUsersEnabled)}
        />
        <AlertRow
          icon={faUsers}
          label="Peak users"
          checked={peakEnabled}
          onChange={() => toggleSetting(PEAK_KEY, 'peak', setPeakEnabled)}
        >
          {peakEnabled && (
            <div className={style.threshold}>
              <span>Peak user count:</span>
              <input
                type="number"
                min={1}
                value={peakThreshold}
                onChange={(e) => {
                  const value = Math.max(1, Number(e.target.value) || DEFAULT_PEAK_THRESHOLD);
                  setPeakThreshold(value);
                  localStorage.setItem(PEAK_THRESHOLD_KEY, String(value));
                  skipNext.peak = true;
                }}
                aria-label="Peak user count"
              />
            </div>
          )}
        </AlertRow>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default AlertsDropdown;
